import { useState, type FormEvent, type ReactNode } from 'react';
import { Trash2 } from 'lucide-react';
import { canRemoveRumContribution, createRumCommentSchema } from '@cercle/shared';
import { useApi, useApp } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus';
import { errorMessage } from '@/shared/http/http-client';
import { Avatar, Button, ErrorText, Muted, Skeleton, Textarea } from '@/shared/ui';
import { formatShortDate } from '../session/format';
import styles from './rum-contributions.module.scss';

/**
 * Le fil de discussion d'une fiche, dans l'ordre d'arrivée.
 *
 * Distinct des « Notes du cercle » juste au-dessus, et volontairement : une note est chiffrée,
 * immuable et ne se dévoile qu'une fois la sienne validée ; un commentaire est libre et se lit
 * sans condition. C'est ce qui manquait — une bouteille se raconte encore des mois après.
 *
 * Rien ne se modifie après coup : un fil s'assume tel qu'il s'est écrit. Chacun retire
 * cependant le sien, et la modération n'importe lequel.
 */
export function RumComments({ rumId }: { rumId: string }): ReactNode {
  const api = useApi();
  const { member } = useApp();
  const online = useOnlineStatus();
  const comments = useAsync(() => api.rums.comments(rumId), [rumId]);

  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function publish(event: FormEvent): Promise<void> {
    event.preventDefault();

    const parsed = createRumCommentSchema.safeParse({ body });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Commentaire invalide.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.rums.addComment(rumId, parsed.data);
      setBody('');
      comments.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function remove(commentId: string): Promise<void> {
    setError(null);
    try {
      await api.rums.removeComment(rumId, commentId);
      comments.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  return (
    <section>
      <h2 className={styles.sectionTitle}>Discussion</h2>

      {comments.loading ? <Skeleton lines={2} /> : null}
      {comments.error ? <ErrorText>{comments.error}</ErrorText> : null}

      {comments.data && comments.data.length > 0 ? (
        <ul className={styles.thread}>
          {comments.data.map((comment) => (
            <li key={comment.id} className={styles.threadItem}>
              <Avatar pseudo={comment.author?.pseudo ?? '?'} url={comment.author?.avatarUrl ?? null} />
              <div>
                <strong>
                  {comment.author?.pseudo ?? 'Membre anonymisé'}
                  <span> · {formatShortDate(comment.createdAt)}</span>
                </strong>
                <p>{comment.body}</p>
              </div>
              {member &&
              canRemoveRumContribution(member.role, member.id, comment.author?.id ?? null) ? (
                <button
                  type="button"
                  className={styles.remove}
                  onClick={() => void remove(comment.id)}
                  aria-label="Retirer ce commentaire"
                >
                  <Trash2 size={15} strokeWidth={1.75} aria-hidden="true" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : comments.loading ? null : (
        <Muted>Personne n’a encore rien dit sur cette bouteille.</Muted>
      )}

      <form className={styles.form} onSubmit={(event) => void publish(event)} noValidate>
        <Textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder="Ce qu’il vous a évoqué, ce que vous en avez appris depuis…"
          rows={3}
          aria-label="Votre commentaire"
        />
        <Button variant="secondary" type="submit" disabled={busy || !body.trim()}>
          {busy ? 'Publication…' : 'Publier'}
        </Button>
      </form>

      <ErrorText>{error}</ErrorText>
      {!online ? (
        <Muted>
          Hors-ligne : un commentaire se publie une fois le réseau revenu. Seules les notes se
          saisissent sans connexion.
        </Muted>
      ) : null}
    </section>
  );
}
